import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'

import { useOccupancyAssign } from '../../../hooks/useRenewalOffers'
import { OccupancyLease, OccupancyUnit } from './types'
import { isOccupancyImmovable } from './occupancyLease'

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingRight: theme.spacing(1),
    },
    filters: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        margin: theme.spacing(0, 0, 1.5),
    },
    unit: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1, 1.25),
        marginBottom: theme.spacing(1),
    },
    unitHead: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.75),
    },
    beds: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
    },
    groupLabel: {
        fontSize: '0.75rem',
        fontWeight: 700,
        margin: theme.spacing(1.5, 0, 0.75),
    },
}))

export function OccupancyAssignDialog({
    lease,
    units,
    leases,
    open,
    onClose,
    title = 'Assign unit',
}: {
    lease: OccupancyLease | null
    units: OccupancyUnit[]
    leases: OccupancyLease[]
    open: boolean
    onClose: () => void
    title?: string
}) {
    const classes = useStyles()
    const assign = useOccupancyAssign()
    const [query, setQuery] = useState('')
    const [configFilter, setConfigFilter] = useState<number | 'all'>('all')

    useEffect(() => {
        if (!open) return
        setQuery('')
        setConfigFilter(lease?.unit_config ?? 'all')
    }, [open, lease?.id, lease?.unit_config])

    const leaseByArea = useMemo(() => {
        const map = new Map<number, OccupancyLease>()
        for (const row of leases) {
            if (row.area_id != null) map.set(row.area_id, row)
        }
        return map
    }, [leases])

    const configOptions = useMemo(() => {
        const seen = new Map<number, string>()
        for (const unit of units) {
            if (!seen.has(unit.unit_config)) {
                seen.set(unit.unit_config, unit.unit_config_name || 'Unit type')
            }
        }
        return Array.from(seen.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { numeric: true }),
            )
    }, [units])

    const visibleUnits = useMemo(() => {
        const needle = query.trim().toLowerCase()
        return units
            .filter((unit) => {
                if (configFilter === 'all') return true
                return unit.unit_config === configFilter
            })
            .filter((unit) => {
                if (!needle) return true
                const occupants = unit.areas
                    .map((area) => leaseByArea.get(area.id)?.tenant_name || '')
                    .join(' ')
                return `${unit.name} ${
                    unit.unit_config_name || ''
                } ${occupants}`
                    .toLowerCase()
                    .includes(needle)
            })
            .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { numeric: true }),
            )
    }, [units, query, configFilter, leaseByArea])

    const grouped = useMemo(() => {
        const byKey = new Map<string, OccupancyUnit[]>()
        for (const unit of visibleUnits) {
            const label = unit.unit_config_name || 'Unit type'
            const existing = byKey.get(label)
            if (existing) existing.push(unit)
            else byKey.set(label, [unit])
        }
        return Array.from(byKey.entries()).sort(([a], [b]) =>
            a.localeCompare(b, undefined, { numeric: true }),
        )
    }, [visibleUnits])

    const handleAssign = (areaId: number) => {
        if (!lease) return
        assign.mutate(
            { lease: lease.id, area: areaId },
            { onSuccess: () => onClose() },
        )
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <Box className={classes.header}>
                <DialogTitle disableTypography>
                    <Typography variant="h6">
                        {title}
                        {lease ? ` — ${lease.tenant_name}` : ''}
                    </Typography>
                    {lease?.unit_config_name && (
                        <Typography variant="body2" color="textSecondary">
                            Floor plan: {lease.unit_config_name}
                        </Typography>
                    )}
                </DialogTitle>
                <IconButton onClick={onClose} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent dividers>
                <Box className={classes.filters}>
                    <Chip
                        size="small"
                        clickable
                        label="All floor plans"
                        color={configFilter === 'all' ? 'primary' : 'default'}
                        onClick={() => setConfigFilter('all')}
                    />
                    {configOptions.map((option) => (
                        <Chip
                            key={option.id}
                            size="small"
                            clickable
                            label={option.name}
                            color={
                                configFilter === option.id
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => setConfigFilter(option.id)}
                        />
                    ))}
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search units or residents"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        fontSize="small"
                                        color="disabled"
                                    />
                                </InputAdornment>
                            ),
                        }}
                        style={{ flex: '1 1 180px' }}
                    />
                </Box>
                {grouped.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        No units match this filter.
                    </Typography>
                ) : (
                    grouped.map(([label, groupUnits]) => (
                        <Box key={label}>
                            <Typography className={classes.groupLabel}>
                                {label}
                            </Typography>
                            {groupUnits.map((unit) => {
                                const filled = unit.areas.filter((area) =>
                                    leaseByArea.has(area.id),
                                ).length
                                return (
                                    <Box key={unit.id} className={classes.unit}>
                                        <Box className={classes.unitHead}>
                                            <Typography
                                                variant="body2"
                                                style={{ fontWeight: 600 }}
                                            >
                                                {unit.name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                {filled}/{unit.areas.length}
                                            </Typography>
                                        </Box>
                                        <Box className={classes.beds}>
                                            {unit.areas.map((area) => {
                                                const occupant = leaseByArea.get(
                                                    area.id,
                                                )
                                                const isCurrent =
                                                    occupant != null &&
                                                    occupant.id === lease?.id
                                                const locked =
                                                    occupant != null &&
                                                    isOccupancyImmovable(
                                                        occupant,
                                                    )
                                                const label =
                                                    occupant == null
                                                        ? `${area.area_config_name} ${area.area_label} · Open`
                                                        : isCurrent
                                                        ? `${occupant.tenant_name} (current)`
                                                        : locked
                                                        ? `${occupant.tenant_name} (renewal)`
                                                        : `Replace ${occupant.tenant_name}`
                                                return (
                                                    <Button
                                                        key={area.id}
                                                        size="small"
                                                        variant={
                                                            occupant
                                                                ? 'outlined'
                                                                : 'contained'
                                                        }
                                                        color="primary"
                                                        disabled={
                                                            locked ||
                                                            isCurrent ||
                                                            assign.isLoading
                                                        }
                                                        onClick={() =>
                                                            handleAssign(
                                                                area.id,
                                                            )
                                                        }
                                                        style={{
                                                            textTransform:
                                                                'none',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {label}
                                                    </Button>
                                                )
                                            })}
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Box>
                    ))
                )}
            </DialogContent>
        </Dialog>
    )
}
